import { ref, onUnmounted } from "vue";
import type { Socket } from "socket.io-client";
import { io } from "socket.io-client";
import { logger } from "~/utils/logger";

export interface CampaignDonationData {
  campaignId: string | number;
  donationId: string;
  amount: number;
  donorName: string | null;
  isAnonymous: boolean;
  message: string | null;
  newTotalAmount: number;
  donorCount: number;
  timestamp: string;
}

export function useCampaignSocket() {
  const socket = ref<Socket | null>(null);
  const isConnected = ref(false);
  const currentCampaignId = ref<string | number | null>(null);
  const config = useRuntimeConfig();

  const connect = () => {
    if (socket.value?.connected) {
      return socket.value;
    }

    const socketUrl = config.public.apiBaseUrl || "http://localhost:4000";

    socket.value = io(socketUrl, {
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      withCredentials: true,
      autoConnect: true,
    });

    socket.value.on("connect", () => {
      isConnected.value = true;
      if (currentCampaignId.value) {
        socket.value!.emit("join:campaign", currentCampaignId.value);
      }
    });

    socket.value.on("disconnect", () => {
      isConnected.value = false;
    });

    socket.value.on("connect_error", (error) => {
      logger.error("WebSocket campaign connect error:", error.message);
    });

    return socket.value;
  };

  /**
   * Salles rejointes. La page cagnotte n'en écoute qu'une, la page d'une
   * association en écoute autant qu'elle affiche de cagnottes — d'où le
   * suivi de l'ensemble, pour toutes les quitter proprement à la fermeture.
   */
  const joinedCampaignIds = ref<Set<string>>(new Set());

  const joinCampaignRoom = (campaignId: string | number) => {
    currentCampaignId.value = campaignId;
    joinedCampaignIds.value.add(String(campaignId));

    if (!socket.value) return;

    if (!socket.value.connected) {
      socket.value.once("connect", () => {
        socket.value!.emit("join:campaign", campaignId);
      });
      return;
    }

    socket.value.emit("join:campaign", campaignId);
  };

  /**
   * Rejoint plusieurs salles d'un coup (page d'association).
   * `currentCampaignId` reste la dernière rejointe, pour ne rien changer au
   * comportement de la page cagnotte qui n'en utilise qu'une.
   */
  const joinCampaignRooms = (campaignIds: Array<string | number>) => {
    for (const id of campaignIds) joinCampaignRoom(id);
  };

  const leaveCampaignRoom = (campaignId?: string | number) => {
    const idToLeave = campaignId || currentCampaignId.value;
    if (!socket.value || !idToLeave) return;

    socket.value.emit("leave:campaign", idToLeave);
    joinedCampaignIds.value.delete(String(idToLeave));
    if (idToLeave === currentCampaignId.value) {
      currentCampaignId.value = null;
    }
  };

  const onCampaignDonation = (callback: (data: CampaignDonationData) => void) => {
    if (!socket.value) return;

    socket.value.on("campaign:donation", (data: CampaignDonationData) => {
      callback(data);
    });

    socket.value.on("campaign:test-donation", (data: CampaignDonationData) => {
      callback(data);
    });
  };

  const disconnect = () => {
    if (socket.value) {
      // Quitter TOUTES les salles rejointes, pas seulement la dernière :
      // sur une page d'association il y en a autant que de cagnottes.
      for (const id of joinedCampaignIds.value) {
        socket.value.emit("leave:campaign", id);
      }
      socket.value.disconnect();
      socket.value = null;
      isConnected.value = false;
      currentCampaignId.value = null;
      joinedCampaignIds.value.clear();
    }
  };

  onUnmounted(() => {
    disconnect();
  });

  return {
    socket,
    isConnected,
    currentCampaignId,
    connect,
    joinCampaignRoom,
    joinCampaignRooms,
    leaveCampaignRoom,
    onCampaignDonation,
    disconnect,
  };
}
